import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { FlaskConical, Plus, Pencil, Trash2 } from 'lucide-react'
import { SearchInput } from '../components/SearchInput'
import { getIngredients, createIngredient, updateIngredient, deleteIngredient } from '../api/brainer'
import { Modal } from '../components/Modal'
import { EmptyState } from '../components/EmptyState'
import { ConfirmDialog } from '../components/ConfirmDialog'
import { useDateFmt } from '../hooks/useDateFmt'
import { Field } from '../components/Field'
import { Button } from '../components/ui/button'
import type { BIngredient } from '../types'

const blank = { name: '', description: '' }

export function BrainerIngredients() {
  const fmt = useDateFmt()
  const qc = useQueryClient()
  const { data: items = [], isLoading } = useQuery({
    queryKey: ['b-ingredients'],
    queryFn: () => getIngredients().then((r) => r.data),
  })

  const [showCreate, setShowCreate] = useState(false)
  const [form, setForm] = useState(blank)

  const create = useMutation({
    mutationFn: () => createIngredient({ name: form.name, description: form.description || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-ingredients'] }); setShowCreate(false); setForm(blank) },
  })

  const [editItem, setEditItem] = useState<BIngredient | null>(null)
  const [editForm, setEditForm] = useState(blank)
  const openEdit = (i: BIngredient) => {
    setEditItem(i)
    setEditForm({ name: i.name, description: i.description ?? '' })
  }

  const save = useMutation({
    mutationFn: () => updateIngredient(editItem!.id, { name: editForm.name, description: editForm.description || undefined }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-ingredients'] }); setEditItem(null) },
  })

  const [search, setSearch] = useState('')
  const filtered = items.filter((i: BIngredient) =>
    [i.name, i.description ?? ''].some((v) => v.toLowerCase().includes(search.toLowerCase()))
  )

  const [deleteTarget, setDeleteTarget] = useState<BIngredient | null>(null)
  const del = useMutation({
    mutationFn: (id: string) => deleteIngredient(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['b-ingredients'] }); setDeleteTarget(null) },
  })

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-ink">Ingredients</h1>
          <p className="text-sm text-ink-dim mt-0.5">
            Type definitions  declare every data type your business uses before building traits
          </p>
        </div>
        <div className="flex items-center gap-2">
          <SearchInput value={search} onChange={setSearch} placeholder="Search ingredients" />
          <Button icon={Plus} onClick={() => setShowCreate(true)}>New ingredient</Button>
        </div>
      </div>

      <div className="table-wrap">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-ink-dim">Loading</div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={FlaskConical}
            title="No ingredients"
            description="Define the data types your business works with  e.g. Text, Integer, Date"
          />
        ) : (
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((item: BIngredient) => (
                <tr key={item.id}>
                  <td className="font-medium text-ink">{item.name}</td>
                  <td className="text-ink-dim">{item.description ?? ''}</td>
                  <td className="text-ink-dim">{fmt(item.createdAt)}</td>
                  <td>
                    <div className="flex items-center gap-3 justify-end">
                      <button onClick={() => openEdit(item)} className="text-ink-dim hover:text-ink transition-colors">
                        <Pencil size={13} />
                      </button>
                      <button onClick={() => setDeleteTarget(item)} className="text-ink-dim hover:text-red-500 transition-colors">
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showCreate && (
        <Modal title="New ingredient" onClose={() => { setShowCreate(false); setForm(blank) }} size="sm">
          <IngredientForm form={form} onChange={setForm} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => { setShowCreate(false); setForm(blank) }}>Cancel</Button>
            <Button
              onClick={() => create.mutate()}
              disabled={!form.name || create.isPending}
              loading={create.isPending}
            >
              Create
            </Button>
          </div>
        </Modal>
      )}

      {editItem && (
        <Modal title="Edit ingredient" onClose={() => setEditItem(null)} size="sm">
          <IngredientForm form={editForm} onChange={setEditForm} />
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="secondary" onClick={() => setEditItem(null)}>Cancel</Button>
            <Button
              onClick={() => save.mutate()}
              disabled={!editForm.name || save.isPending}
              loading={save.isPending}
            >
              Save
            </Button>
          </div>
        </Modal>
      )}

      {deleteTarget && (
        <ConfirmDialog
          title="Delete ingredient"
          message={`Delete "${deleteTarget.name}"? Traits using this ingredient will be affected.`}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={() => del.mutate(deleteTarget.id)}
          loading={del.isPending}
        />
      )}
    </div>
  )
}

function IngredientForm({
  form,
  onChange,
}: {
  form: { name: string; description: string }
  onChange: (f: any) => void
}) {
  return (
    <div className="space-y-4">
      <Field label="Name *">
        <input
          value={form.name}
          onChange={(e) => onChange((f: any) => ({ ...f, name: e.target.value }))}
          placeholder="e.g. Text, Integer, Float, Date"
          className="field"
        />
        <p className="text-xs text-ink-dim mt-1">The type name  this is what traits will reference.</p>
      </Field>
      <Field label="Description">
        <input
          value={form.description}
          onChange={(e) => onChange((f: any) => ({ ...f, description: e.target.value }))}
          placeholder="e.g. A string of characters, A number with no decimals"
          className="field"
        />
      </Field>
    </div>
  )
}
